/**
 * Set documenti per paese/regime/cliente (fonte di verità unica, D11):
 * governa SIA la checklist del carico SIA la generazione del plico.
 * Risoluzione: riga cliente > riga paese > default regime.
 */

import { Loader2, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3EliminaSetDocumenti,
  useM3SetDocumenti,
  useM3UpsertSetDocumenti,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { SetDocumentiRow } from "@/types/api";

const INPUT =
  "h-8 rounded-md border bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring";

function scopeLabel(r: SetDocumentiRow): string {
  if (r.cliente_codice != null) return `cliente ${r.cliente_codice}`;
  if (r.paese_iso) return `paese ${r.paese_iso}`;
  return `regime ${r.regime}`;
}

export function SetDocumentiSection() {
  const { data: righe, isLoading } = useM3SetDocumenti();
  const upsert = useM3UpsertSetDocumenti();
  const elimina = useM3EliminaSetDocumenti();
  const [aggiungi, setAggiungi] = useState(false);
  const [nuovo, setNuovo] = useState({
    regime: "bielorussia",
    tipo_documento: "altro",
    label: "",
    provenienza: "generato",
    stadio_generazione: "da_ndp",
    template_codice: "",
    ordine: 99,
  });

  const gruppi = useMemo(() => {
    const m = new Map<string, SetDocumentiRow[]>();
    for (const r of righe ?? []) {
      const k = scopeLabel(r);
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()];
  }, [righe]);

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          Set documenti (checklist + generazione)
        </h2>
        <button
          type="button"
          onClick={() => setAggiungi(!aggiungi)}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-accent"
        >
          <Plus className="size-3.5" /> Riga
        </button>
      </div>
      <p className="max-w-3xl text-xs text-muted-foreground">
        Per ogni destinazione: quali documenti servono, se arrivano da Cube
        (ricevuti) o li genera l'agente, e in quale stadio. La riga più
        specifica vince: cliente &gt; paese &gt; regime.
      </p>

      {aggiungi && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/20 p-2">
          <select
            className={INPUT}
            value={nuovo.regime}
            onChange={(e) => setNuovo({ ...nuovo, regime: e.target.value })}
          >
            {["italia", "ue", "extra_ue", "bielorussia"].map((r) => (
              <option key={r} value={r}>
                regime {r}
              </option>
            ))}
          </select>
          <input
            className={cn(INPUT, "w-40")}
            placeholder="tipo (es. cmr)"
            value={nuovo.tipo_documento}
            onChange={(e) => setNuovo({ ...nuovo, tipo_documento: e.target.value })}
          />
          <input
            className={cn(INPUT, "w-48")}
            placeholder="Etichetta"
            value={nuovo.label}
            onChange={(e) => setNuovo({ ...nuovo, label: e.target.value })}
          />
          <select
            className={INPUT}
            value={nuovo.provenienza}
            onChange={(e) => setNuovo({ ...nuovo, provenienza: e.target.value })}
          >
            <option value="ricevuto">ricevuto (Cube)</option>
            <option value="generato">generato (M3)</option>
          </select>
          {nuovo.provenienza === "generato" && (
            <>
              <select
                className={INPUT}
                value={nuovo.stadio_generazione}
                onChange={(e) =>
                  setNuovo({ ...nuovo, stadio_generazione: e.target.value })
                }
              >
                <option value="da_ndp">bozza da NDP</option>
                <option value="da_ddt">solo da DDT</option>
              </select>
              <input
                className={cn(INPUT, "w-44")}
                placeholder="template_codice"
                value={nuovo.template_codice}
                onChange={(e) =>
                  setNuovo({ ...nuovo, template_codice: e.target.value })
                }
              />
            </>
          )}
          <button
            type="button"
            disabled={!nuovo.tipo_documento || upsert.isPending}
            onClick={() => {
              upsert.mutate({
                body: {
                  regime: nuovo.regime as SetDocumentiRow["regime"],
                  tipo_documento: nuovo.tipo_documento as SetDocumentiRow["tipo_documento"],
                  label: nuovo.label || null,
                  provenienza: nuovo.provenienza as SetDocumentiRow["provenienza"],
                  stadio_generazione:
                    nuovo.provenienza === "generato"
                      ? (nuovo.stadio_generazione as SetDocumentiRow["stadio_generazione"])
                      : null,
                  template_codice:
                    nuovo.provenienza === "generato" && nuovo.template_codice
                      ? nuovo.template_codice
                      : null,
                  ordine: nuovo.ordine,
                },
              });
              setAggiungi(false);
            }}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {upsert.isPending && <Loader2 className="size-3 animate-spin" />}
            Aggiungi
          </button>
        </div>
      )}

      <div className="space-y-4">
        {gruppi.map(([scope, rows]) => (
          <div key={scope}>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {scope}
            </h3>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-left text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">Documento</th>
                    <th className="px-2 py-1.5 font-medium">Provenienza</th>
                    <th className="px-2 py-1.5 font-medium">Stadio</th>
                    <th className="px-2 py-1.5 font-medium">Lingue</th>
                    <th className="px-2 py-1.5 font-medium">Firma</th>
                    <th className="px-2 py-1.5 font-medium">Obblig.</th>
                    <th className="px-2 py-1.5 font-medium">Attivo</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={cn("border-t", !r.attivo && "opacity-45")}>
                      <td className="px-2 py-1.5">
                        {r.label ?? r.tipo_documento}
                        {r.template_codice && (
                          <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                            {r.template_codice}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.provenienza === "ricevuto" ? "Cube" : "M3"}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.stadio_generazione === "da_ndp"
                          ? "bozza da NDP"
                          : r.stadio_generazione === "da_ddt"
                            ? "da DDT"
                            : "—"}
                        {r.timing === "post_partenza" && " · post-partenza"}
                      </td>
                      <td className="px-2 py-1.5">{r.lingue?.join(", ") ?? "—"}</td>
                      <td className="px-2 py-1.5">
                        {r.firma_richiesta_da !== "nessuna" ? r.firma_richiesta_da : "—"}
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={r.obbligatorio}
                          onChange={(e) =>
                            upsert.mutate({
                              id: r.id,
                              body: { ...r, obbligatorio: e.target.checked },
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={r.attivo}
                          onChange={(e) =>
                            upsert.mutate({
                              id: r.id,
                              body: { ...r, attivo: e.target.checked },
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Eliminare «${r.label ?? r.tipo_documento}» da ${scope}?`)) {
                              elimina.mutate(r.id);
                            }
                          }}
                          className="text-muted-foreground hover:text-destructive"
                          title="Elimina riga"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
